import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { apiClient } from '../store/authStore';
import {
  MagnifyingGlass, CheckCircle, ArrowRight, ArrowUp, ShareNetwork,
  Spinner, ArrowLeft, Confetti,
} from '@phosphor-icons/react';

const C = {
  bg:'#0a0a0f', surface:'#13131f', deep:'#0d0d18', input:'#1a1a2e',
  border:'rgba(124,58,237,0.2)', borderHi:'rgba(124,58,237,0.4)',
  text:'#e8e8f0', muted:'#9090b0', hint:'#5a5a7a',
  violet:'#7c3aed', blue:'#3b82f6',
  grad:'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
  green:'#10b981', red:'#f87171', amber:'#fbbf24',
};

function Ring({ value, color, label }) {
  return (
    <div style={{ textAlign:'center' }}>
      <div style={{ width:120, height:120, position:'relative', margin:'0 auto' }}>
        <svg style={{ width:'100%', height:'100%', transform:'rotate(-90deg)' }} viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(124,58,237,0.15)" strokeWidth="8" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={`${(value / 100) * 264} 264`} style={{ transition:'stroke-dasharray 1s ease' }} />
        </svg>
        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:34, fontWeight:900, fontFamily:"'JetBrains Mono', monospace", color }}>{value}</div>
      </div>
      <div style={{ fontSize:13, color:C.hint, marginTop:8 }}>{label}</div>
    </div>
  );
}

export default function BeforeAfterPage() {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [data, setData] = useState(state && state.fixes ? {
    score_before: state.scoreBefore, url: state.url,
    fixes_applied: state.fixes,
    score_gain: (state.fixes || []).reduce((s, f) => s + (f.points || 0) * 0, 0),
  } : null);
  const [loading, setLoading] = useState(!data);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (data) return;
    apiClient.get(`/audit/before-after/${analysisId}`)
      .then(d => setData({ ...d, fixes_applied: d.fixes_applied || [] }))
      .catch(() => setData({ score_before: 0, fixes_applied: [], url: '' }))
      .finally(() => setLoading(false));
  }, [analysisId, data]);

  if (loading) return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <Spinner className="animate-spin" style={{ width:28, height:28, color:C.violet }} />
    </div>
  );

  const fixes = data.fixes_applied || [];
  const gain = data.score_gain != null && data.score_gain > 0
    ? data.score_gain
    : fixes.reduce((s, f) => s + (f.points || 0), 0);
  const before = data.score_before || 0;
  const after = Math.min(100, before + gain);
  const host = data.url ? (() => { try { return new URL(data.url).hostname; } catch { return data.url; } })() : '';

  const share = async () => {
    try { await navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  return (
    <div style={{ minHeight:'100vh', background:C.bg, fontFamily:"'IBM Plex Sans', system-ui, sans-serif", color:C.text }}>
      <header style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, position:'sticky', top:0, zIndex:50 }}>
        <div style={{ maxWidth:920, margin:'0 auto', padding:'0 24px', height:56, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Link to="/" style={{ display:'flex', alignItems:'center', gap:10, textDecoration:'none' }}>
            <div style={{ width:34, height:34, background:C.grad, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <MagnifyingGlass weight="bold" style={{ width:16, height:16, color:'#fff' }} />
            </div>
            <span style={{ fontSize:17, fontWeight:800, fontFamily:"'Satoshi', sans-serif", color:C.text }}>SEO Dash 360</span>
          </Link>
          <button onClick={() => navigate(`/report/${analysisId}`)} style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, color:C.muted, cursor:'pointer', fontFamily:'inherit' }}>
            <ArrowLeft style={{ width:13, height:13 }} /> Back to report
          </button>
        </div>
      </header>

      <main style={{ maxWidth:920, margin:'0 auto', padding:'40px 24px' }}>
        <div style={{ textAlign:'center', marginBottom:8 }}>
          <Confetti weight="fill" style={{ width:40, height:40, color:C.violet, margin:'0 auto 10px' }} />
          <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:28, fontWeight:900, margin:'0 0 6px', letterSpacing:'-0.02em' }}>Your improvement{host ? `, ${host}` : ''}</h1>
          <p style={{ fontSize:14, color:C.muted, margin:0 }}>Here's the impact of the fixes you just applied.</p>
        </div>

        {/* Score comparison */}
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:'32px 24px', margin:'24px 0', display:'flex', alignItems:'center', justifyContent:'center', gap:40, flexWrap:'wrap' }}>
          <Ring value={before} color={C.hint} label="Before" />
          <div style={{ textAlign:'center' }}>
            <ArrowRight weight="bold" style={{ width:30, height:30, color:C.violet }} />
            {gain > 0 && (
              <div style={{ display:'inline-flex', alignItems:'center', gap:4, marginTop:8, padding:'4px 12px', background:'rgba(16,185,129,0.12)', border:'1px solid rgba(16,185,129,0.3)', borderRadius:20, fontSize:13, fontWeight:800, color:C.green }}>
                <ArrowUp weight="bold" style={{ width:13, height:13 }} /> +{gain} pts
              </div>
            )}
          </div>
          <Ring value={after} color={C.violet} label="After" />
        </div>

        {/* Fixes applied */}
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'20px 22px', marginBottom:20 }}>
          <h2 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:16, fontWeight:800, margin:'0 0 14px' }}>Fixes applied</h2>
          {fixes.length === 0 ? (
            <p style={{ fontSize:13, color:C.hint, margin:0 }}>No fixes recorded for this audit yet.</p>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {fixes.map((f, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:C.deep, border:`1px solid ${C.border}`, borderRadius:9, padding:'12px 14px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <CheckCircle weight="fill" style={{ width:18, height:18, color:C.green }} />
                    <span style={{ fontSize:14, color:C.text }}>{f.label}{f.count ? ` (${f.count})` : ''}</span>
                  </div>
                  <span style={{ fontSize:13, fontWeight:700, color:C.green }}>+{f.points} pts</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
          <button onClick={share} style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'11px 20px', background:'transparent', border:`1px solid ${C.borderHi}`, borderRadius:9, fontSize:13.5, fontWeight:700, color:C.text, cursor:'pointer', fontFamily:'inherit' }}>
            <ShareNetwork weight="fill" style={{ width:15, height:15 }} /> {copied ? 'Link copied!' : 'Share your improvement'}
          </button>
          <button onClick={() => navigate('/analysis/new')} style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'11px 20px', background:C.grad, border:'none', borderRadius:9, fontSize:13.5, fontWeight:700, color:'#fff', cursor:'pointer', boxShadow:'0 2px 12px rgba(124,58,237,0.3)', fontFamily:'inherit' }}>
            <MagnifyingGlass weight="bold" style={{ width:15, height:15 }} /> Run new audit
          </button>
        </div>
      </main>
    </div>
  );
}
